import { parseQueryPreservingPlus, splitUrlOnce } from '@common/core-layer/utils/url-query-parse.utils'

export const SHOW_BETA_PREVIEW_QUERY_KEY = 'show-beta-preview' as const

const LOGIN_PATH = '/login'

function serializeQuery(params: Record<string, string>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value)
    }
  }
  return search.toString()
}

function buildUrl(pathname: string, params: Record<string, string>): string {
  const queryString = serializeQuery(params)
  return queryString ? `${pathname}?${queryString}` : pathname
}

/** Picks a single string out of `route.query`-style values (string | string[] | undefined). */
function pickFirst(value: string | string[] | undefined | null): string | null {
  if (value == null) return null
  const firstEntry = Array.isArray(value) ? value[0] : value
  if (firstEntry === undefined || firstEntry === null) return null
  const stringValue = String(firstEntry)
  return stringValue === '' ? null : stringValue
}

/**
 * Read the flag from a Vue Router-style query object. Returns `null` when the key
 * is absent or empty so callers can use a single null-check.
 */
export function getShowBetaPreviewFromQuery(query: Record<string, string | string[] | undefined>): string | null {
  return pickFirst(query[SHOW_BETA_PREVIEW_QUERY_KEY])
}

/**
 * Read the flag from a raw `path?query` string. Handles the common nested case
 * `/login?redirect=<encoded URL containing the flag>` by parsing one level of
 * `redirect=`. Mirrors `getMagicLinkPayloadFromFullPath`'s resolution shape.
 */
export function getShowBetaPreviewFromUrl(rawUrl: string): string | null {
  const [path, queryString] = splitUrlOnce(rawUrl)
  const params = parseQueryPreservingPlus(queryString)

  const direct = params[SHOW_BETA_PREVIEW_QUERY_KEY]
  if (direct !== undefined && direct !== '') return direct

  if (path === LOGIN_PATH && params.redirect) {
    const redirectUrlParts = splitUrlOnce(params.redirect)
    const innerQuery = redirectUrlParts[1]
    const innerParams = parseQueryPreservingPlus(innerQuery)
    const nested = innerParams[SHOW_BETA_PREVIEW_QUERY_KEY]
    if (nested !== undefined && nested !== '') return nested
  }

  return null
}

/**
 * Returns the URL with `show-beta-preview` removed from both the top-level query
 * and any nested `redirect=` value. Other query params are preserved as-is.
 */
export function stripShowBetaPreviewFromUrl(rawUrl: string): string {
  const [path, queryString] = splitUrlOnce(rawUrl)
  const params = parseQueryPreservingPlus(queryString)

  let touched = false

  if (params[SHOW_BETA_PREVIEW_QUERY_KEY] !== undefined) {
    delete params[SHOW_BETA_PREVIEW_QUERY_KEY]
    touched = true
  }

  if (path === LOGIN_PATH && params.redirect) {
    const [innerPath, innerQuery] = splitUrlOnce(params.redirect)
    const innerParams = parseQueryPreservingPlus(innerQuery)
    if (innerParams[SHOW_BETA_PREVIEW_QUERY_KEY] !== undefined) {
      delete innerParams[SHOW_BETA_PREVIEW_QUERY_KEY]
      params.redirect = buildUrl(innerPath || '/', innerParams)
      touched = true
    }
  }

  if (!touched) return rawUrl

  return buildUrl(path, params)
}

/**
 * Returns the URL with `show-beta-preview=<value>` appended (or replaced if
 * already present) at the top level. Use after building an auth-redirect URL so
 * the flag survives an SSR redirect into the app's hydration middleware.
 */
export function appendShowBetaPreviewToUrl(rawUrl: string, value: string): string {
  const [path, queryString] = splitUrlOnce(rawUrl)
  const params = parseQueryPreservingPlus(queryString)
  params[SHOW_BETA_PREVIEW_QUERY_KEY] = value
  return buildUrl(path, params)
}

/** `true | 1 | yes` (case-insensitive) → true. Anything else → false. */
export function isBetaPreviewEnabled(value: string | null | undefined): boolean {
  if (value == null) return false
  const normalized = value.toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
}
